import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { useIsMobile } from '@/hooks/use-mobile'
import { Spinner } from '@/components/ui/spinner'
import { ChatMainArea } from 'components/chat-page/ChatMainArea'
import { NotebookSidebar } from 'components/notebook/NotebookSidebar'
import { NotebookPanel } from 'components/notebook/NotebookPanel'
import { ResizablePanel } from 'components/notebook/ResizablePanel'
import { DocumentPreviewContext, PreviewDocument, PreviewKind } from 'components/notebook/DocumentPreviewContext'
import { ModalOutlet } from 'providers/ModalProvider'
import { PortalToastContainer } from 'components/ui/PortalToastContainer'
import { CookieConsent } from 'components/cookie/CookieConsent'
import { messagesActions, messagesSelectors } from 'store/messages'
import { useGetAgents } from 'api/agents'
import { openNewChat } from 'api/chats'
import { getSignedUrl } from 'api/uploads'
import { useSendBotQuestion } from 'hooks/useSendBotQuestion'
import { getAreaIdSearchParams, getNotebookHistoryParam } from 'utils/query-params'
import { getSourceTitle } from 'utils/citations'
import { AgentPrompt } from 'types/agent'
import { CitationSource } from 'types/chat-message'

const getAgentNameParam = () => new URLSearchParams(window.location.search).get('agent') ?? ''
const getContextIdParam = () => new URLSearchParams(window.location.search).get('contextId') ?? undefined

/** Right panel width: prompt cards vs an open PDF preview. */
const PANEL_WIDTH = 340
const PANEL_WIDTH_DOCUMENT = 460

/**
 * Notebook mode (`/notebook?agent=<name>&contextId=<ctx>`): history on the left, the regular chat UI
 * in the middle, the agent's canned prompts (or the PDF the user clicked) on the right. Same
 * providers/auth bootstrap as /chat; the chat is created at the first message (typed or prompt
 * card) and locked to the agent via `agentId`.
 */
export const NotebookPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const areaId = useMemo(getAreaIdSearchParams, [])
  const agentName = useMemo(getAgentNameParam, [])
  const contextId = useMemo(getContextIdParam, [])
  const historyOpen = useMemo(getNotebookHistoryParam, [])
  const chatId = useSelector(messagesSelectors.chatId)
  const { data: agents, isSuccess, isError } = useGetAgents()
  const { sendQuestion, sending, botTyping } = useSendBotQuestion()
  const isMobile = useIsMobile()
  const [panelOpen, setPanelOpen] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | undefined>(undefined)

  // Sources render inside the shared message components: this is how a clicked PDF reaches the right panel.
  const openPreview = useCallback((source: CitationSource, kind: PreviewKind) => {
    const sourceUrl = source.url ?? ''
    // Panel first, url later: the click opens it at once and a spinner covers the signing round trip.
    setPreviewDoc({ name: getSourceTitle(source), kind, sourceUrl })
    setPanelOpen(true)
    // the iframe NAVIGATES to the signed url: no fetch, so no CORS (the CDN caches the file without
    // the CORS header as soon as one navigation — a new tab — fills the cache first)
    getSignedUrl(sourceUrl)
      // a late answer for a document already closed or replaced by another click is dropped
      .then((url) => setPreviewDoc((doc) => (doc?.sourceUrl === sourceUrl ? { ...doc, url } : doc)))
      .catch(console.error)
  }, [])

  const closePreview = useCallback(() => setPreviewDoc(undefined), [])

  // On mobile the document is the whole sheet: closing it means going back to the chat, not to the prompts.
  const onCloseDocument = useCallback(() => {
    closePreview()
    if (isMobile) setPanelOpen(false)
  }, [closePreview, isMobile])

  const agent = useMemo(() => agents?.find((a) => a.name === agentName), [agents, agentName])

  // A prompt clicked before the chat exists is queued here and flushed once the new chat's messages
  // are loaded (same mechanism ChatMainArea uses for the hero's first typed message).
  const pendingQuestionRef = useRef<string | undefined>(undefined)
  const onMessagesLoaded = () => {
    const question = pendingQuestionRef.current
    if (!question) return
    pendingQuestionRef.current = undefined
    sendQuestion(question, { source: 'default_question' }).catch(console.error)
  }

  // chatId is persisted: never resume a /chat conversation (possibly another agent) in notebook mode.
  useEffect(() => {
    dispatch(messagesActions.setChatId(undefined))
  }, [dispatch])

  const onPromptClick = (prompt: AgentPrompt) => {
    setPanelOpen(false)
    if (chatId) {
      sendQuestion(prompt.text, { source: 'default_question' }).catch(console.error)
      return
    }
    pendingQuestionRef.current = prompt.text
    openNewChat({ areaId, agentId: agent?._id, contextId }).catch((e) => {
      console.error({ e })
      pendingQuestionRef.current = undefined
    })
  }

  const onNewAnalysis = () => {
    dispatch(messagesActions.setChatId(undefined))
    closePreview()
  }

  if (isError || (isSuccess && !agent)) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background text-muted-foreground">
        {t('notebookAgentUnavailable')}
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background">
        <Spinner />
      </div>
    )
  }

  const panel = (
    <NotebookPanel
      agent={agent}
      disabled={sending || botTyping}
      onPromptClick={onPromptClick}
      doc={previewDoc}
      onCloseDocument={onCloseDocument}
    />
  )

  return (
    <SidebarProvider
      defaultOpen={historyOpen}
      className="h-dvh min-h-0 overflow-hidden bg-background font-sans text-foreground"
      style={{ '--sidebar-width': '300px' } as React.CSSProperties}
    >
      <NotebookSidebar agent={agent} contextId={contextId} onNewAnalysis={onNewAnalysis} />

      <SidebarInset className="min-w-0 bg-background">
        <DocumentPreviewContext.Provider value={openPreview}>
          <ChatMainArea
            areaId={areaId}
            agentId={agent._id}
            contextId={contextId}
            onMessagesLoaded={onMessagesLoaded}
            headerActions={
              // Mobile only: opens the right panel as a sheet. Inline in the header row, same
              // 36px box as the menu button next to it, so it takes that spot when the menu is absent.
              <Button
                variant="ghost"
                size="icon-lg"
                className="md:hidden"
                onClick={() => setPanelOpen(true)}
                title={t('notebookPromptsTitle')}
              >
                <OdinIcon icon={['far', 'sparkles']} className="h-4 w-4" />
              </Button>
            }
          />
        </DocumentPreviewContext.Provider>
        <PortalToastContainer topOffset={16} />
      </SidebarInset>

      {isMobile ? (
        <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
          {/* With a document open the panel header has its own close button (it closes the document
              AND the sheet, see onCloseDocument): the sheet's X would sit right on it. */}
          <SheetContent
            side="right"
            showCloseButton={!previewDoc}
            className="w-[340px] gap-0 bg-background p-0 text-foreground"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t('notebookPromptsTitle')}</SheetTitle>
              <SheetDescription>{agent.name}</SheetDescription>
            </SheetHeader>
            {panel}
          </SheetContent>
        </Sheet>
      ) : (
        // Prompts ↔ PDF glides to that mode's width; a drag survives switching between PDFs.
        <ResizablePanel defaultWidth={previewDoc ? PANEL_WIDTH_DOCUMENT : PANEL_WIDTH}>{panel}</ResizablePanel>
      )}

      <CookieConsent surface="page" />
      <ModalOutlet variant="dialog" />
    </SidebarProvider>
  )
}
